import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
} from "../../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import {
  computePassportSheetGrid,
  getPassportSheetSize,
} from "../config/passportPhotoMakerFeatures";
import "./PassportSheetPreview.css";

/** Space reserved under the sheet for size/copies caption. */
const CAPTION_RESERVE_PX = 48;

/**
 * Right-pane preview of a printable passport sheet (tiled copies).
 * Orientation is chosen for max copies; tiles keep country aspect ratio.
 */
const PassportSheetPreview = ({
  imageUrl = null,
  sheetSizeId = "A4",
  photoWidthMm = 35,
  photoHeightMm = 45,
  loading = false,
  loadingLabel = "Working…",
  emptyLabel = "Upload a portrait photo to preview your passport photo",
}) => {
  const rootRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 320, height: 420 });

  const sheet = useMemo(
    () => getPassportSheetSize(sheetSizeId),
    [sheetSizeId]
  );

  const grid = useMemo(() => {
    if (!sheet) {
      return computePassportSheetGrid({
        sheetWidthMm: 210,
        sheetHeightMm: 297,
        photoWidthMm,
        photoHeightMm,
      });
    }
    return computePassportSheetGrid({
      sheetWidthMm: sheet.widthMm,
      sheetHeightMm: sheet.heightMm,
      photoWidthMm,
      photoHeightMm,
    });
  }, [sheet, photoWidthMm, photoHeightMm]);

  const sheetAspect = useMemo(() => {
    const w = grid.sheetWidthMm || 210;
    const h = grid.sheetHeightMm || 297;
    return w / h;
  }, [grid.sheetWidthMm, grid.sheetHeightMm]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const measure = () => {
      const fitted = computeFittedStageSize(
        el.clientWidth,
        Math.max(80, el.clientHeight - CAPTION_RESERVE_PX),
        sheetAspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [sheetAspect, loading, imageUrl]);

  const tileLayout = useMemo(() => {
    const sw = grid.sheetWidthMm || 1;
    const sh = grid.sheetHeightMm || 1;
    const pw = grid.photoWidthMm || photoWidthMm;
    const ph = grid.photoHeightMm || photoHeightMm;
    const gutter = grid.gutterMm ?? 2;
    const cols = grid.cols || 1;
    const rows = grid.rows || 1;

    const gridW = cols * pw + (cols - 1) * gutter;
    const gridH = rows * ph + (rows - 1) * gutter;
    const startX = (sw - gridW) / 2;
    const startY = (sh - gridH) / 2;

    const tiles = [];
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        tiles.push({
          key: `${r}-${c}`,
          left: `${((startX + c * (pw + gutter)) / sw) * 100}%`,
          top: `${((startY + r * (ph + gutter)) / sh) * 100}%`,
          width: `${(pw / sw) * 100}%`,
          height: `${(ph / sh) * 100}%`,
        });
      }
    }
    return { tiles };
  }, [grid, photoWidthMm, photoHeightMm]);

  const sizeLabel = useMemo(() => {
    const fmt = (n) => {
      const v = Math.round(Number(n) * 10) / 10;
      return Number.isInteger(v) ? String(v) : String(v);
    };
    // Catalog sheet size (unrotated), not the packed orientation dims.
    const w = sheet?.widthMm ?? grid.sheetWidthMm;
    const h = sheet?.heightMm ?? grid.sheetHeightMm;
    return `${fmt(w)}×${fmt(h)}mm`;
  }, [sheet, grid.sheetWidthMm, grid.sheetHeightMm]);

  const copiesLabel =
    grid.count === 1 ? "1 copy" : `${grid.count} copies`;

  return (
    <ToolPrepCanvasStage
      className="appm-sheet-preview"
      ref={rootRef}
      metaImageUrl={imageUrl}
    >
      {loading ? (
        <div className="appm-sheet-preview__loader">
          <RoundLoader />
          <p>{loadingLabel}</p>
        </div>
      ) : null}

      {!loading && !imageUrl ? (
        <div
          className="appm-sheet-preview__empty"
          style={{ width: stageSize.width, height: stageSize.height }}
          aria-label="Empty preview"
        >
          <p>{emptyLabel}</p>
        </div>
      ) : null}

      {!loading && imageUrl ? (
        <div className="appm-sheet-preview__stack">
          <div
            className="appm-sheet-preview__sheet"
            style={{ width: stageSize.width, height: stageSize.height }}
            role="img"
            aria-label={`${sheet?.label || "Print"} sheet with ${grid.count} photos`}
          >
            <div className="appm-sheet-preview__canvas">
              {tileLayout.tiles.map((tile) => (
                <div
                  key={tile.key}
                  className="appm-sheet-preview__cell"
                  style={{
                    left: tile.left,
                    top: tile.top,
                    width: tile.width,
                    height: tile.height,
                  }}
                >
                  <img src={imageUrl} alt="" draggable={false} />
                </div>
              ))}
            </div>
          </div>
          <div className="appm-sheet-preview__caption">
            <span>
              {sheet?.label || sheetSizeId} · {sizeLabel}
            </span>
            <span>{copiesLabel}</span>
          </div>
        </div>
      ) : null}
    </ToolPrepCanvasStage>
  );
};

export default PassportSheetPreview;
